import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { getUVEState } from '@dotcms/uve';
import { Observable, Subject, combineLatest, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

import { PresidioImpersonationFacade } from 'src/app/extensions/presidio-demo/services/presidio-impersonation.facade';
import { PresidioWarehouseCustomerService } from 'src/app/extensions/presidio-demo/services/presidio-warehouse-customer.service';
import { PresidioDiscoveryFacade } from 'src/app/extensions/presidio-discovery/facades/presidio-discovery.facade';
import { PRESIDIO_HOME_CONTENT } from 'ish-shared/dias/home/presidio-home-content';
import { AccountFacade } from 'ish-core/facades/account.facade';
import { AppFacade } from 'ish-core/facades/app.facade';
import { DeviceType } from 'ish-core/models/viewtype/viewtype.types';
import { DesignViewService } from 'ish-core/utils/design-view/design-view.service';
import { FeatureToggleService } from 'ish-core/utils/feature-toggle/feature-toggle.service';
import { SearchBoxComponent } from 'ish-shared/components/search/search-box/search-box.component.all';
import { PresidioShellUiFacade } from 'ish-shared/dias/shell-chrome/presidio-shell-ui.facade';

type CollapsibleComponent = 'search' | 'navbar' | 'minibasket';

/**
 * CUSTOMIZATION: Header search + portal nav.
 * - Guest / shopping: product catalog search (MVP 2.1.3).
 * - Logged-in + `presidioOmnisearch`: shown on all routes; typeahead spans products,
 *   orders, invoices, and quotes (prototype scope delta A14).
 * - Punchout (cXML/OCI): hide Orders / Invoices / Contracts / Reports in portal nav
 *   (MVP 2.5.x — shop + quote transfer; My Account already gates these).
 */
@Component({
  selector: 'custom-header-default',
  templateUrl: './custom-header-default.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./custom-header-default.component.scss'],
  standalone: false,
})
export class CustomHeaderDefaultComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  readonly isSticky = input(false);
  readonly deviceType = input<DeviceType>(undefined);
  readonly reset = input<unknown>(undefined);
  /**
   * Portal Shop destination — catalog root CLP listing all shopping top-level categories
   * (Computers & Hardware, Displays & AV, …). Same root as home Shop CTA.
   * Routed as DIAS `/c/{id}` (not legacy `/category/{id}`).
   */
  readonly catalogRootCategoryId = PRESIDIO_HOME_CONTENT.hero.primaryCategoryId || 'SHOP';
  isLoggedIn$: Observable<boolean>;
  displayName$: Observable<string>;
  companyName$: Observable<string>;
  /** MVP 8.2.2 — CSR Co-Browse control (Quinn / mock CSR session). */
  canImpersonate$: Observable<boolean>;
  allDrawerOpen$: Observable<boolean>;
  /** True when header search chrome should render (shopping and/or omnisearch). */
  showProductSearch$: Observable<boolean>;
  /** Portal Shop nav underline — CLP / PLP / SRP / PDP / cart (not Overview/home / My Warehouse). */
  shopNavActive$: Observable<boolean>;
  /** MVP 2.6.3 — My Warehouse portal nav (warehouse accounts only). */
  isWarehouseCustomer$: Observable<boolean>;
  /** Underline My Warehouse when on /category/WH (held-stock PLP). */
  warehouseNavActive$: Observable<boolean>;
  /** Contracts Dashboard underline — contracts, devices, and work items. */
  contractsAssetsNavActive$: Observable<boolean>;
  /** MVP 1.2.4 / 1.2.6 — pending purchase + user-access approvals for Approvals badge. */
  pendingApprovalsCount$: Observable<number>;
  /**
   * FR2.2.1 — when desktop portal links cannot fit one row, use the All (hamburger) drawer
   * instead of wrapping the horizontal nav onto a second line.
   */
  useHamburgerNav = false;
  activeComponent: CollapsibleComponent = 'search';
  showSearchBox = false;
  isInsideUveEditor = !!getUVEState();
  readonly searchBoxComponent = viewChild(SearchBoxComponent);
  readonly searchBoxWrapper = viewChild<ElementRef>('searchBoxWrapper');
  readonly mobileSearchBoxWrapper = viewChild<ElementRef>('mobileSearchBoxWrapper');
  readonly portalNavList = viewChild<ElementRef<HTMLElement>>('portalNavList');
  readonly portalNav = viewChild<ElementRef<HTMLElement>>('portalNav');
  shouldFocusSearch = false;
  private destroy$ = new Subject<void>();
  private readonly cdRef = inject(ChangeDetectorRef);
  private portalNavResizeObserver?: ResizeObserver;
  private portalNavMutationObserver?: MutationObserver;
  private portalNavFitRaf = 0;

  /**
   * Header search placeholder — omnisearch copy when feature is on.
   * Phone/tablet use a shorter string so the placeholder is not clipped by the submit button.
   */
  get searchPlaceholderKey(): string {
    const omnisearch = this.featureToggleService.enabled('presidioOmnisearch');
    if (!omnisearch) {
      return 'search.product.instructional_text';
    }
    const compact = this.deviceType() === 'mobile' || this.deviceType() === 'tablet';
    return compact
      ? 'search.omnisearch.instructional_text.compact'
      : 'search.omnisearch.instructional_text';
  }

  constructor(
    private accountFacade: AccountFacade,
    private featureToggleService: FeatureToggleService,
    private appFacade: AppFacade,
    private designViewService: DesignViewService,
    private shellUi: PresidioShellUiFacade,
    private impersonationFacade: PresidioImpersonationFacade,
    private warehouseCustomer: PresidioWarehouseCustomerService,
    private discoveryFacade: PresidioDiscoveryFacade,
    private router: Router
  ) {}

  ngOnInit() {
    this.isLoggedIn$ = this.accountFacade.isLoggedIn$;
    this.allDrawerOpen$ = this.shellUi.allDrawerOpen$;
    this.canImpersonate$ = this.impersonationFacade.canImpersonate$;
    this.isWarehouseCustomer$ = this.warehouseCustomer.isWarehouseCustomer$;
    this.pendingApprovalsCount$ = this.discoveryFacade.pendingApprovalsCount$;
    const onShoppingRoute$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.isShoppingProductSearchUrl(this.router.url)),
      distinctUntilChanged()
    );
    const omnisearchEnabled = this.featureToggleService.enabled('presidioOmnisearch');
    this.showProductSearch$ = combineLatest([this.isLoggedIn$, onShoppingRoute$]).pipe(
      map(([loggedIn, onShopping]) => (omnisearchEnabled && loggedIn) || onShopping),
      distinctUntilChanged()
    );
    this.shopNavActive$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.isShopNavActiveUrl(this.router.url)),
      distinctUntilChanged()
    );
    this.warehouseNavActive$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.isWarehouseNavActiveUrl(this.router.url)),
      distinctUntilChanged()
    );
    this.contractsAssetsNavActive$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.isContractsAssetsNavActiveUrl(this.router.url)),
      distinctUntilChanged()
    );
    this.showProductSearch$.pipe(takeUntil(this.destroy$)).subscribe(show => {
      if (!show) {
        this.hideSearchBox();
      }
    });
    // Refresh Approvals badge after navigation (approve/reject, login, account switch).
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.discoveryFacade.refresh();
      });
    this.displayName$ = this.accountFacade.user$.pipe(
      map(user => {
        if (!user) {
          return '';
        }
        const full = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        return full || user.login || '';
      })
    );
    // Company name comes from the logged-in customer (affiliate context after sales-rep switch)
    this.companyName$ = this.accountFacade.customer$.pipe(map(customer => customer?.companyName || ''));
    this.subscribeToSearchBoxState();
    this.discoveryFacade.refresh();

    // Re-measure when auth/warehouse/approvals change the set of portal links.
    combineLatest([this.isLoggedIn$, this.isWarehouseCustomer$, this.pendingApprovalsCount$])
      .pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe(() => this.schedulePortalNavFitCheck());
  }

  ngAfterViewInit() {
    this.bindPortalNavFitObservers();
    this.schedulePortalNavFitCheck();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.reset) {
      this.resetActiveComponent();
      this.onCloseSearchBox();
    }
    this.toggleSpecialStatusOfSearch();
    if (changes.deviceType) {
      this.schedulePortalNavFitCheck();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.teardownPortalNavFitObservers();
    if (this.portalNavFitRaf) {
      cancelAnimationFrame(this.portalNavFitRaf);
      this.portalNavFitRaf = 0;
    }
  }

  /** Hamburger opens All drawer with the same portal destinations. */
  get showPortalNavHamburger(): boolean {
    return this.deviceType() !== 'desktop' || this.useHamburgerNav;
  }

  get showSearch() {
    return this.activeComponent === 'search' && (this.deviceType() === 'mobile' || this.isSticky());
  }

  /**
   * Legacy expandable-search flag. Compact shopping search is persistent (Baymard);
   * kept for sticky/desktop interactions that still toggle the box.
   */
  get mobileSearchOpen() {
    return this.showSearchBox && this.deviceType() !== 'desktop';
  }

  get showNavBar() {
    return this.activeComponent === 'navbar' || this.deviceType() === 'desktop';
  }

  toggle(component: CollapsibleComponent) {
    this.activeComponent = this.activeComponent === component ? this.getDefaultActiveComponent() : component;
  }

  openAllDrawer(): void {
    this.shellUi.openAllDrawer();
  }

  toggleAllDrawer(): void {
    this.shellUi.toggleAllDrawer();
  }

  openContact(): void {
    this.shellUi.openContact();
  }

  isDotCmsEditView(): boolean {
    return this.featureToggleService.enabled('dotCmsUve') && !!this.isInsideUveEditor;
  }

  // emitdotcmsHomePageEditEvent() {
  //   if (this.isInsideIframe()) {
  //     emitDotCmsEMAEvent('/index');
  //   }
  // }

  // isInsideIframe(): boolean {
  //   return isSiteEmbedded();
  // }

  showOrHideSearchBox() {
    this.showSearchBox = !this.showSearchBox;
    this.appFacade.setSeachActive(this.showSearchBox);

    if (this.showSearchBox && this.deviceType() !== 'desktop') {
      // Temporarily disable shouldFocusSearch to force re-evaluation
      this.shouldFocusSearch = false;

      requestAnimationFrame(() => {
        this.shouldFocusSearch = true;
        this.focusSearchInput();
      });
    } else {
      this.shouldFocusSearch = false;
      if (!this.showSearchBox) {
        this.blurSearchInput();
      }
    }
  }

  hideSearchBox() {
    this.showSearchBox = false;
    this.appFacade.setSeachActive(false);
    this.searchBoxComponent()?.blurSearchInput?.(); // optional
  }

  searchBoxActive() {
    this.showSearchBox = true;
  }

  getSearchBoxAnimationClass() {
    return {
      'search-active': this.showSearchBox,
      'search-inactive': !this.showSearchBox,
    };
  }

  getHomePageUrl() {
    return this.isInsideUveEditor ? (this.designViewService.isDesignViewMode() ? '/home' : '/page/index') : '/home';
  }

  private subscribeToSearchBoxState() {
    this.appFacade
      .getSeachActive()
      .pipe(takeUntil(this.destroy$))
      .subscribe(active => (this.showSearchBox = active));
  }

  private resetActiveComponent() {
    this.activeComponent = 'search';
  }

  private toggleSpecialStatusOfSearch() {
    const isSticky = this.isSticky();
    if (isSticky && this.activeComponent === 'search') {
      this.activeComponent = undefined;
    }
    if (!isSticky && !this.activeComponent) {
      this.activeComponent = 'search';
    }
  }

  private getDefaultActiveComponent(): CollapsibleComponent {
    return this.isSticky() ? undefined : 'search';
  }

  onCloseSearchBox(): void {
    if (this.deviceType() !== 'desktop' && this.showSearchBox) {
      this.blurSearchInput();
      this.showSearchBox = false;
      this.appFacade.setSeachActive(false);
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (this.deviceType() !== 'desktop') {
      const clickedInside = this.getSearchBoxWrapperElement()?.contains(event.target as Node);
      const clickedToggle = (event.target as HTMLElement).closest('.search-toggle-icon');

      // If click is outside search box and not on the toggle button
      if (!clickedInside && !clickedToggle && this.showSearchBox) {
        this.onCloseSearchBox();
      }
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.schedulePortalNavFitCheck();
  }

  private bindPortalNavFitObservers(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.portalNavResizeObserver = new ResizeObserver(() => this.schedulePortalNavFitCheck());
      const nav = this.portalNav()?.nativeElement;
      if (nav) {
        this.portalNavResizeObserver.observe(nav);
      }
    }
    if (typeof MutationObserver !== 'undefined') {
      this.portalNavMutationObserver = new MutationObserver(() => this.schedulePortalNavFitCheck());
      const list = this.portalNavList()?.nativeElement;
      if (list) {
        this.portalNavMutationObserver.observe(list, { childList: true, subtree: true, characterData: true });
      }
    }
  }

  private teardownPortalNavFitObservers(): void {
    this.portalNavResizeObserver?.disconnect();
    this.portalNavResizeObserver = undefined;
    this.portalNavMutationObserver?.disconnect();
    this.portalNavMutationObserver = undefined;
  }

  private schedulePortalNavFitCheck(): void {
    if (this.portalNavFitRaf) {
      cancelAnimationFrame(this.portalNavFitRaf);
    }
    this.portalNavFitRaf = requestAnimationFrame(() => {
      this.portalNavFitRaf = 0;
      this.updatePortalNavFit();
    });
  }

  /**
   * Prefer hamburger over a wrapped second row. Uses nowrap + scrollWidth so we can
   * keep measuring while the visible row is replaced by the menu toggle.
   */
  private updatePortalNavFit(): void {
    if (this.deviceType() !== 'desktop') {
      if (this.useHamburgerNav) {
        this.useHamburgerNav = false;
        this.cdRef.markForCheck();
      }
      return;
    }

    const list = this.portalNavList()?.nativeElement;
    if (!list) {
      return;
    }

    // Re-bind observers if the nav was recreated after a deviceType flip.
    if (this.portalNavResizeObserver && this.portalNav()?.nativeElement) {
      this.portalNavResizeObserver.observe(this.portalNav().nativeElement);
    }
    if (this.portalNavMutationObserver && list) {
      this.portalNavMutationObserver.observe(list, { childList: true, subtree: true, characterData: true });
    }

    const overflows = list.scrollWidth > list.clientWidth + 2;
    if (overflows !== this.useHamburgerNav) {
      this.useHamburgerNav = overflows;
      this.cdRef.markForCheck();
      // After switching to measure-only / visible, confirm with a second frame.
      this.portalNavFitRaf = requestAnimationFrame(() => {
        this.portalNavFitRaf = 0;
        const list2 = this.portalNavList()?.nativeElement;
        if (!list2 || this.deviceType() !== 'desktop') {
          return;
        }
        const stillOverflows = list2.scrollWidth > list2.clientWidth + 2;
        if (stillOverflows !== this.useHamburgerNav) {
          this.useHamburgerNav = stillOverflows;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  /**
   * Only one of both search box variants is rendered at a time, depending on the 'sparqueSearch' feature toggle.
   */
  private getSearchBoxWrapperElement(): HTMLElement {
    return this.searchBoxWrapper()?.nativeElement ?? this.mobileSearchBoxWrapper()?.nativeElement;
  }

  private focusSearchInput(): void {
    this.getMobileSearchInput()?.focus();
  }

  private blurSearchInput(): void {
    this.searchBoxComponent()?.blurSearchInput?.();
    this.getMobileSearchInput()?.blur();
  }

  /**
   * The standard search box is lazily rendered and offers no focus API, so its input is addressed directly.
   */
  private getMobileSearchInput(): HTMLInputElement {
    return this.getSearchBoxWrapperElement()?.querySelector('input.searchTerm');
  }

  /**
   * Product catalog search surfaces (MVP 2.1.3), including cart — used for guests
   * and as the shopping baseline when omnisearch is off.
   */
  private isShoppingProductSearchUrl(url: string): boolean {
    const path = this.normalizePath(url);
    if (path === '/' || path === '' || path === '/home' || path.startsWith('/home/')) {
      return true;
    }
    return this.isShopNavActiveUrl(url);
  }

  /**
   * Shop portal nav is active on catalog (CLP/PLP), search results, PDP, and cart.
   * My Warehouse (`/category/WH`) uses its own nav highlight.
   */
  private isShopNavActiveUrl(url: string): boolean {
    if (this.isWarehouseNavActiveUrl(url)) {
      return false;
    }
    const path = this.normalizePath(url);
    if (path === '/c' || path.startsWith('/c/')) {
      return true;
    }
    if (path === '/category' || path.startsWith('/category/')) {
      return true;
    }
    if (path === '/categoryref' || path.startsWith('/categoryref/')) {
      return true;
    }
    if (path === '/search' || path.startsWith('/search/')) {
      return true;
    }
    if (path === '/p' || path.startsWith('/p/')) {
      return true;
    }
    if (path === '/basket' || path.startsWith('/basket/')) {
      return true;
    }
    return false;
  }

  /** MVP 2.6.3 — My Warehouse PLP route. */
  private isWarehouseNavActiveUrl(url: string): boolean {
    const path = this.normalizePath(url).toLowerCase();
    return (
      path === '/category/wh' ||
      path.startsWith('/category/wh/') ||
      path === '/category/my-warehouse' ||
      path.startsWith('/category/my-warehouse/')
    );
  }

  /**
   * Contracts Dashboard portal nav stays active across the breadcrumb trail:
   * Contracts Dashboard → Devices → Work items.
   */
  private isContractsAssetsNavActiveUrl(url: string): boolean {
    const path = this.normalizePath(url).toLowerCase();
    return (
      path === '/account/contracts' ||
      path.startsWith('/account/contracts/') ||
      path === '/account/devices' ||
      path.startsWith('/account/devices/') ||
      path === '/account/workitems' ||
      path.startsWith('/account/workitems/')
    );
  }

  private normalizePath(url: string): string {
    return (url || '').split('?')[0].split(';')[0] || '';
  }
}
